/**
 * Stripe Checkout return URL (`/{tenant}/checkout/complete?paymentId=…&session_id=…`).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { CUSTOMER_TAB_CONTENT_CLASS, CUSTOMER_CONTENT_NARROW_CLASS } from '../../app/customerTopBarStyles.js';
import { Card } from '../../shared/ui/Card.js';
import { Button } from '../../shared/ui/Button.js';
import { useTenantCode } from '../../shared/tenant/TenantProvider.js';
import { useCustomerSession } from '../../shared/session/CustomerSessionProvider.js';
import { stripOidcLandFromSearch } from '../../shared/session/returnTarget.js';
import { usePaymentStatus, usePaymentStatusByCheckoutSession, useGetCheckoutSession } from './checkoutApi.js';
import { FIO_PAYMENT_STATUS_POLL_MS } from './paymentPollPolicy.js';
import { RefundRequestCta } from './RefundRequestCta.js';
import { CheckoutReceiptPanel } from './CheckoutReceiptPanel.js';
import { usePaymentPendingScope } from '../../app/pwa/usePaymentPendingScope.js';
import { clearJourneyCheckpoint } from '../../app/pwa/journeyCheckpoint.js';
import { resetShopCart } from '../shop/cart/resetShopCart.js';
import { resolvePostAuthFallbackPath } from '../../navigation/navigationEngine.js';
import { readLastKnownSalesPointId } from '../../shared/session/lastKnownSalesPoint.js';
import { ReceiptProvider } from '../../shared/context/ReceiptContext.js';
import { isSignedInNavChrome } from '../hub/customerHubPaths.js';
import { useLeavePaymentSurfaceOnAuthLoss } from './useLeavePaymentSurfaceOnAuthLoss.js';
import { PostPurchaseFavoritePrompt } from './PostPurchaseFavoritePrompt.js';
import { readLastKnownSalesPointSlug } from '../../shared/session/lastKnownSalesPoint.js';

/** Show extended guidance if confirmation takes longer than webhook + reconcile window. */
const CHECKOUT_COMPLETE_LONG_WAIT_MS = 90_000;

function CheckoutCompletePendingCopy(): JSX.Element {
  const { t } = useTranslation('customer');
  const [longWait, setLongWait] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setLongWait(true);
    }, CHECKOUT_COMPLETE_LONG_WAIT_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, []);

  return (
    <p className="text-sm text-[var(--color-on-surface-muted)]" aria-live="polite">
      {longWait ? t('checkout.completePendingLong') : t('checkout.completePending')}
    </p>
  );
}

export function CheckoutCompleteRoute(): JSX.Element {
  const { t } = useTranslation('customer');
  const tenantCode = useTenantCode();
  const navigate = useNavigate();
  const location = useLocation();
  // U1/S5 / G5: login upsell when !isSignedInNavChrome (guest under R1).
  const { session } =
    useCustomerSession();
  const signedInChrome = isSignedInNavChrome({ session });
  const [params] = useSearchParams();
  const paymentId = params.get('paymentId');
  const sessionId = params.get('session_id');
  const viewReceiptParam = params.get('view') === 'receipt';

  const resolvedPaymentId =
    paymentId !== null && paymentId.length > 0 ? paymentId : null;
  const resolvedSessionId =
    sessionId !== null && sessionId.length > 0
      ? sessionId
      : null;

  const hasPaymentRef =
    (paymentId !== null && paymentId.length > 0) ||
    (sessionId !== null && sessionId.length > 0);

  const statusByPaymentId = usePaymentStatus(resolvedPaymentId, FIO_PAYMENT_STATUS_POLL_MS, {
    enabled: resolvedPaymentId !== null});
  const statusBySession = usePaymentStatusByCheckoutSession(resolvedSessionId, {
    enabled: resolvedSessionId !== null && resolvedPaymentId === null});
  const statusQuery =
    resolvedPaymentId !== null ? statusByPaymentId : statusBySession;
  const checkoutSessionPublicId =
    statusQuery.data?.checkoutSessionPublicId ?? resolvedSessionId;
  const checkoutSessionQuery = useGetCheckoutSession(checkoutSessionPublicId, 'complete');
  const cartResetRef = useRef(false);

  const displayPaymentId = statusQuery.data?.paymentId ?? resolvedPaymentId;
  const pendingPaymentKey =
    displayPaymentId ?? resolvedPaymentId ?? resolvedSessionId ?? '';
  // G5 — stable complete-route surface key when a payment/session ref exists.
  const authLossSurfaceKey =
    hasPaymentRef && pendingPaymentKey.length > 0
      ? `complete:${pendingPaymentKey}`
      : '';
  const { withholdPaymentChrome } = useLeavePaymentSurfaceOnAuthLoss({
    tenantCode,
    surfaceKey: authLossSurfaceKey});

  const payStatus = statusQuery.data?.status ?? 'PENDING';
  const isCompleted = payStatus === 'COMPLETED';
  const isFailed = payStatus === 'FAILED' || payStatus === 'EXPIRED';
  const isPending =
    (resolvedPaymentId !== null || resolvedSessionId !== null) &&
    !isCompleted &&
    !isFailed &&
    !statusQuery.isError &&
    !withholdPaymentChrome;

  const paymentPendingCheckpoint =
    isPending
      ? {
          returnTo: `${location.pathname}${location.search}`,
          paymentId: resolvedPaymentId ?? undefined,
          journey: 'J-PAYMENT'}
      : undefined;
  usePaymentPendingScope(isPending, paymentPendingCheckpoint);

  useEffect(() => {
    if (isCompleted || isFailed) {
      clearJourneyCheckpoint(tenantCode);
    }
  }, [isCompleted, isFailed, tenantCode]);

  useEffect(() => {
    if (!isCompleted || cartResetRef.current) {
      return;
    }
    const kioskId =
      checkoutSessionQuery.data?.kioskId ??
      checkoutSessionQuery.data?.salesPointId ??
      readLastKnownSalesPointId(tenantCode);
    if (kioskId === null || kioskId <= 0) {
      return;
    }
    cartResetRef.current = true;
    resetShopCart({
      tenantCode,
      kioskId,
      source: 'checkout-complete'});
  }, [checkoutSessionQuery.data, isCompleted, tenantCode]);

  const [receiptPanelOpen, setReceiptPanelOpen] = useState(viewReceiptParam);

  const returnPathWithQuery = useMemo(() => {
    const search = stripOidcLandFromSearch(
      location.search.length > 0 ? location.search : '',
    );
    const base = `/${encodeURIComponent(tenantCode)}/checkout/complete${search}`;
    if (search.includes('view=receipt')) {
      return base;
    }
    const separator = search.length > 0 ? '&' : '?';
    return `${base}${separator}view=receipt`;
  }, [location.search, tenantCode]);

  const showReceiptGate = isCompleted && statusQuery.data !== undefined;

  const salesPointId = useMemo(() => {
    const kioskId = checkoutSessionQuery.data?.kioskId ?? null;
    return (
      checkoutSessionQuery.data?.salesPointId ??
      kioskId ??
      readLastKnownSalesPointId(tenantCode)
    );
  }, [checkoutSessionQuery.data, tenantCode]);

  // G5 — auth-loss leave: stub only (no pending payment chrome). Hook navigates via useLayoutEffect.
  if (withholdPaymentChrome) {
    return (
      <div className={`${CUSTOMER_TAB_CONTENT_CLASS} ${CUSTOMER_CONTENT_NARROW_CLASS}`}>
        <p
          className="p-4 text-sm text-[var(--color-on-surface-muted)]"
          role="status"
          data-testid="checkout-complete-auth-loss-redirect"
        >
          {t('checkout.redirecting', { defaultValue: t('account.sessionHydrating') })}
        </p>
      </div>
    );
  }

  const cardBody = (
    <Card padded>
      <div className="flex flex-col gap-3">
        {!hasPaymentRef ? (
          <p className="text-sm text-[var(--color-danger)]" role="alert">
            {t('checkout.completeMissingParams')}
          </p>
        ) : null}
        {hasPaymentRef && isPending && pendingPaymentKey.length > 0 ? (
          <CheckoutCompletePendingCopy key={pendingPaymentKey} />
        ) : null}
        {hasPaymentRef && isCompleted ? (
          <>
            {showReceiptGate ? (
              <p
                className="text-sm font-medium text-[var(--color-success)]"
                data-testid="checkout-complete-success"
              >
                {t('checkout.completeSuccess')}
              </p>
            ) : (
              <p className="text-sm text-[var(--color-on-surface-muted)]">
                {t('checkout.completeSuccess')}
              </p>
            )}
          </>
        ) : null}
        {hasPaymentRef && isFailed ? (
          <>
            <p className="text-sm text-[var(--color-danger)]" role="alert">
              {t('checkout.completeFailed')}
            </p>
            {displayPaymentId !== null && displayPaymentId.length > 0 ? (
              <RefundRequestCta
                paymentId={displayPaymentId}
                refundEligible={statusQuery.data?.refundEligible === true}
              />
            ) : null}
          </>
        ) : null}
        {hasPaymentRef && statusQuery.isError ? (
          <p className="text-sm text-[var(--color-danger)]" role="alert">
            {t('checkout.completeStatusError')}
          </p>
        ) : null}
        {displayPaymentId !== null && displayPaymentId.length > 0 ? (
          <p className="text-xs text-[var(--color-on-surface-muted)]">
            {t('checkout.completePaymentId', { paymentId: displayPaymentId })}
          </p>
        ) : null}

        {showReceiptGate ? (
          <>
            {!receiptPanelOpen ? (
              <Button
                intent="primary"
                size="lg"
                block
                data-testid="customer-success-view-receipt"
                onClick={() => {
                  setReceiptPanelOpen(true);
                }}
              >
                {t('checkout.success.viewReceipt')}
              </Button>
            ) : (
              <CheckoutReceiptPanel
                paymentStatus={statusQuery.data}
                returnPathWithQuery={returnPathWithQuery}
                salesPointId={salesPointId}
              />
            )}
            {statusQuery.data?.transactionId != null &&
            statusQuery.data.transactionId > 0 ? (
              <Button
                intent="secondary"
                size="lg"
                block
                data-testid="customer-success-open-durable-confirmation"
                onClick={() => {
                  const transactionId = statusQuery.data?.transactionId;
                  if (transactionId == null || transactionId <= 0) {
                    return;
                  }
                  navigate(`/account/orders/${transactionId}/confirmation`);
                }}
              >
                {t('account.durableConfirmation.openLink')}
              </Button>
            ) : null}
            <Button
              intent="secondary"
              size="lg"
              block
              data-testid="customer-success-back-shop"
              onClick={() => {
                navigate(resolvePostAuthFallbackPath(tenantCode));
              }}
            >
              {t('checkout.success.backToShop')}
            </Button>
            {!signedInChrome && !receiptPanelOpen ? (
              <Card padded>
                <p className="text-sm text-[var(--color-on-surface-muted)]">
                  {t('checkout.success.loginUpsell')}
                </p>
              </Card>
            ) : null}
          </>
        ) : (
          <Button
            intent="primary"
            size="lg"
            block
            onClick={() => {
              navigate(resolvePostAuthFallbackPath(tenantCode));
            }}
          >
            {t('checkout.completeBackToShop')}
          </Button>
        )}
      </div>
    </Card>
  );

  const receiptScope =
    displayPaymentId !== null && displayPaymentId.length > 0
      ? {
          paymentId: displayPaymentId,
          checkoutSessionPublicId: checkoutSessionPublicId ?? null,
          returnPathWithQuery}
      : null;

  const content = (
    <div className={`${CUSTOMER_TAB_CONTENT_CLASS} ${CUSTOMER_CONTENT_NARROW_CLASS}`}>
      <header>
        <h1 className="text-lg font-semibold text-[var(--color-on-surface)]">
          {t('checkout.completeTitle')}
        </h1>
        <p className="text-sm text-[var(--color-on-surface-muted)]">
          {t('checkout.completeSubtitle')}
        </p>
      </header>
      {cardBody}
      {showReceiptGate &&
      isCompleted &&
      salesPointId !== null &&
      salesPointId > 0 ? (
        <div className="mt-4">
          <PostPurchaseFavoritePrompt
            tenantCode={tenantCode}
            salesPointId={salesPointId}
            salesPointSlug={readLastKnownSalesPointSlug(tenantCode) ?? undefined}
            enabled={true}
          />
        </div>
      ) : null}
    </div>
  );

  if (receiptScope === null) {
    return content;
  }

  return <ReceiptProvider scope={receiptScope}>{content}</ReceiptProvider>;
}
